/**
 * kbProvisioning — pure, React-free helpers for the "create a knowledge base by
 * file upload" flow (Feature B-2). The create flow provisions a section-scoped
 * KB (StartKnowledgeBaseProvisioning) then POLLS DescribeExecution until the
 * Step Functions execution is terminal — the SAME poll approach the Goal KB
 * panel uses (see features/goals/useGoalKbProvisioning). These helpers mirror
 * the goal phase semantics but live in the knowledgeBases feature so the two
 * features stay decoupled.
 *
 * Consumed by {@link ./useKnowledgeBases}.
 */

/** The high-level status a create-by-upload job surfaces to the UI. */
export type CreateKbPhase =
  | 'idle'
  | 'provisioning'
  | 'uploading'
  | 'ingesting'
  | 'ready'
  | 'error';

/** The resolved provisioning phase derived from a Step Functions status. */
export type ProvisioningPhase = 'running' | 'ready' | 'error';

const TERMINAL_STATUSES = new Set([
  'SUCCEEDED',
  'FAILED',
  'TIMED_OUT',
  'ABORTED',
]);

/** True when a Step Functions execution status is terminal (polling can stop). */
export function isTerminalExecutionStatus(status: string): boolean {
  return TERMINAL_STATUSES.has((status ?? '').trim().toUpperCase());
}

/**
 * Maps a Step Functions execution status to a provisioning phase: SUCCEEDED →
 * ready; FAILED/TIMED_OUT/ABORTED → error; RUNNING and any non-terminal/unknown
 * value → running.
 */
export function provisioningPhase(status: string): ProvisioningPhase {
  const s = (status ?? '').trim().toUpperCase();
  if (s === 'SUCCEEDED') return 'ready';
  if (s === 'FAILED' || s === 'TIMED_OUT' || s === 'ABORTED') return 'error';
  return 'running';
}

/**
 * Sanitizes a user-entered KB name into a logical section/bundle key used BOTH
 * as the `sectionKey` for StartKnowledgeBaseProvisioning AND as the bundle-key
 * override for the follow-up UploadDocument / StartIngestion / grounded queries
 * (matching how the Goal KB reuses one key for provisioning and routing).
 * Lower-cased; runs of non-alphanumerics collapse to single hyphens; leading/
 * trailing hyphens trimmed.
 */
export function sanitizeSectionKey(name: string): string {
  return (name ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** True when a name yields a non-empty section key (min to create a KB). */
export function isValidSectionKey(name: string): boolean {
  return sanitizeSectionKey(name).length > 0;
}
